import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./MeetingScheduler.css";

/* ---------------------------------------------------------------------------
   Toplanti planlama (2026-09-14)

   Desen Google Takvim + Meet ile ayni:
     - Toplanti ONCE zamanli bir kayit olarak olusur,
     - katilim baglantisi o anda hazirdir (sonradan uretilmez),
     - davetliler bildirim alir ve yanit verebilir (evet / belki / hayir),
     - toplanti hem MeMeet'te hem Takvim modulunde gorunur.

   Sema zaten hazirdi (memeet_rooms.meeting_start_date_time / timezone);
   eksik olan tek sey arayuzdu: canlida 486 odanin SIFIRI gelecek tarihliydi.
--------------------------------------------------------------------------- */

export type UpcomingMeeting = {
  roomID: string;
  title: string;
  startsAt: number;      // epoch saniye
  endsAt: number;
  timezone?: string;
  isPrivate?: boolean;
  isHost?: boolean;
  isLive?: boolean;
  attendeeCount?: number;
  responseStatus?: number;  // 0 bekliyor · 1 evet · 2 hayir · 4 belki
  joinLink?: string;
};

type Translate = (key: string, fallback?: string) => string;

/** Tarayicinin saat dilimi. Sunucu serbest metin sakliyor. */
const localTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** "2026-09-20" + "14:30" -> epoch saniye (YEREL saat dilimi). */
export const toEpochSeconds = (dateStr: string, timeStr: string): number => {
  if (!dateStr || !timeStr) return 0;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return 0;
  return Math.floor(new Date(y, m - 1, d, hh, mm, 0, 0).getTime() / 1000);
};

/** Bir sonraki yarim saat: varsayilan baslangic. */
const nextHalfHour = (): Date => {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + (30 - (d.getMinutes() % 30)));
  return d;
};

const pad = (n: number) => String(n).padStart(2, "0");
const dateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeValue = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Listede gosterilecek kisa tarih: "Bugun 14:30" / "Sal 20 Eyl 14:30". */
export const formatWhen = (startsAt: number, locale: string): string => {
  const d = new Date(startsAt * 1000);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  if (isTomorrow) return `+1 ${time}`;
  return `${d.toLocaleDateString(locale, { day: "2-digit", month: "short" })} ${time}`;
};

const DURATIONS = [15, 30, 45, 60, 90, 120];

export function ScheduleMeetingModal({
  open,
  onClose,
  onSchedule,
  t,
}: {
  open: boolean;
  onClose: () => void;
  onSchedule: (payload: {
    title: string;
    startDateTime: number;
    endDateTime: number;
    timezone: string;
    isPrivate: boolean;
    attendees: Array<{ email: string }>;
  }) => Promise<{ ok: boolean; joinLink?: string; message?: string }>;
  t: Translate;
}) {
  const initial = useMemo(() => nextHalfHour(), []);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(dateValue(initial));
  const [time, setTime] = useState(timeValue(initial));
  const [duration, setDuration] = useState(30);
  const [isPrivate, setIsPrivate] = useState(false);
  const [guests, setGuests] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fresh = nextHalfHour();
    setTitle("");
    setDate(dateValue(fresh));
    setTime(timeValue(fresh));
    setDuration(30);
    setIsPrivate(false);
    setGuests("");
    setError(null);
    setCreatedLink(null);
    setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = useCallback(async () => {
    if (busy) return;
    const start = toEpochSeconds(date, time);
    if (!start) { setError(t("memeetScheduleInvalidTime", "Pick a date and time.")); return; }
    if (start < Math.floor(Date.now() / 1000) - 60) {
      setError(t("memeetScheduleInPast", "That time is already past."));
      return;
    }
    // Virgul, noktali virgul, bosluk veya satir sonu ile ayrilmis e-postalar.
    const attendees = guests
      .split(/[,;\s\n]+/)
      .map((x) => x.trim())
      .filter((x) => x.includes("@"))
      .map((email) => ({ email }));

    setBusy(true);
    setError(null);
    const res = await onSchedule({
      title: title.trim() || t("memeetScheduleDefaultTitle", "Meeting"),
      startDateTime: start,
      endDateTime: start + duration * 60,
      timezone: localTimezone(),
      isPrivate,
      attendees,
    });
    setBusy(false);
    if (res.ok) setCreatedLink(res.joinLink || "");
    else setError(res.message || t("memeetScheduleFailed", "Could not schedule the meeting."));
  }, [busy, date, time, guests, title, duration, isPrivate, onSchedule, t]);

  const copyLink = useCallback(async () => {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { /* pano reddedildi: kullanici elle secebilir */ }
  }, [createdLink]);

  if (!open) return null;

  return (
    <div className="memeet-schedule-overlay" onClick={onClose}>
      <div className="memeet-schedule" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="memeet-schedule-title">{t("memeetScheduleTitle", "Schedule a meeting")}</h3>

        {createdLink !== null ? (
          <div className="memeet-schedule-done">
            <p className="memeet-schedule-done-text">
              {t("memeetScheduleCreated", "Your meeting is scheduled. Guests have been notified.")}
            </p>
            {createdLink ? (
              <div className="memeet-schedule-linkrow">
                <input className="memeet-schedule-input" readOnly value={createdLink} />
                <button className="meet-button" onClick={copyLink}>
                  {copied ? t("memeetCopied", "Copied") : t("memeetCopyLink", "Copy link")}
                </button>
              </div>
            ) : null}
            <div className="memeet-schedule-actions">
              <button className="meet-button primary" onClick={onClose}>{t("memeetDone", "Done")}</button>
            </div>
          </div>
        ) : (
          <>
            <label className="memeet-schedule-field">
              <span>{t("memeetScheduleName", "Title")}</span>
              <input
                className="memeet-schedule-input"
                value={title}
                maxLength={120}
                placeholder={t("memeetScheduleDefaultTitle", "Meeting")}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
            </label>

            <div className="memeet-schedule-row">
              <label className="memeet-schedule-field">
                <span>{t("memeetScheduleDate", "Date")}</span>
                <input className="memeet-schedule-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="memeet-schedule-field">
                <span>{t("memeetScheduleTime", "Time")}</span>
                <input className="memeet-schedule-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </label>
              <label className="memeet-schedule-field">
                <span>{t("memeetScheduleDuration", "Duration")}</span>
                <select className="memeet-schedule-input" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                  {DURATIONS.map((d) => (
                    <option key={d} value={d}>{d} {t("memeetMinutesShort", "min")}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="memeet-schedule-field">
              <span>{t("memeetScheduleGuests", "Guests")}</span>
              <textarea
                className="memeet-schedule-input memeet-schedule-textarea"
                value={guests}
                placeholder={t("memeetScheduleGuestsHint", "email@example.com, other@example.com")}
                onChange={(e) => setGuests(e.target.value)}
                rows={2}
              />
            </label>

            <label className="memeet-schedule-check">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
              <span>{t("memeetSchedulePrivate", "Require a passcode")}</span>
            </label>

            {error ? <div className="memeet-schedule-error" role="alert">{error}</div> : null}

            <div className="memeet-schedule-actions">
              <button className="meet-button" onClick={onClose} disabled={busy}>{t("memeetCancel", "Cancel")}</button>
              <button className="meet-button primary" onClick={submit} disabled={busy}>
                {busy ? t("memeetScheduling", "Scheduling…") : t("memeetScheduleSubmit", "Schedule")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function UpcomingMeetings({
  meetings,
  loading,
  locale,
  onJoin,
  onRespond,
  onCancel,
  t,
}: {
  meetings: UpcomingMeeting[];
  loading?: boolean;
  locale: string;
  onJoin: (roomID: string) => void;
  onRespond?: (roomID: string, response: "yes" | "no" | "maybe") => void;
  onCancel?: (roomID: string) => void;
  t: Translate;
}) {
  if (!loading && meetings.length === 0) return null;

  return (
    <section className="memeet-upcoming">
      <h3 className="memeet-upcoming-title">{t("memeetUpcoming", "Upcoming")}</h3>
      {loading ? (
        <div className="memeet-upcoming-empty">…</div>
      ) : (
        <ul className="memeet-upcoming-list">
          {meetings.map((m) => (
            <li key={m.roomID} className={`memeet-upcoming-item ${m.isLive ? "is-live" : ""}`}>
              <div className="memeet-upcoming-when">
                {m.isLive ? t("memeetLiveNow", "Now") : formatWhen(m.startsAt, locale)}
              </div>
              <div className="memeet-upcoming-body">
                <div className="memeet-upcoming-name" title={m.title}>{m.title}</div>
                <div className="memeet-upcoming-meta">
                  {m.isHost ? t("memeetYouHost", "You are the host") : t("memeetInvited", "Invited")}
                  {m.attendeeCount ? ` · ${m.attendeeCount} ${t("memeetGuestsShort", "guests")}` : ""}
                </div>
              </div>
              <div className="memeet-upcoming-actions">
                {!m.isHost && onRespond ? (
                  <select
                    className="memeet-upcoming-rsvp"
                    value={m.responseStatus === 1 ? "yes" : m.responseStatus === 2 ? "no" : m.responseStatus === 4 ? "maybe" : ""}
                    onChange={(e) => e.target.value && onRespond(m.roomID, e.target.value as "yes" | "no" | "maybe")}
                    title={t("memeetRsvp", "Response")}
                  >
                    <option value="">{t("memeetRsvpPending", "Respond")}</option>
                    <option value="yes">{t("memeetRsvpYes", "Yes")}</option>
                    <option value="maybe">{t("memeetRsvpMaybe", "Maybe")}</option>
                    <option value="no">{t("memeetRsvpNo", "No")}</option>
                  </select>
                ) : null}
                {m.isHost && onCancel ? (
                  <button className="meet-button ghost" onClick={() => onCancel(m.roomID)}>
                    {t("memeetCancelMeeting", "Cancel")}
                  </button>
                ) : null}
                <button className="meet-button primary" onClick={() => onJoin(m.roomID)}>
                  {t("memeetJoin", "Join")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
